import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Camera, User, Save, X, Loader2, Volume2, Vibrate } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { PREF_SOUND, PREF_VIBRATE, testRestSound } from '../components/RestTimer';
import axios from 'axios';
import { API, setStoredUser, avatarUrl } from '../utils/api';
import { toast } from 'sonner';

export const EditProfilePage = ({ user, onUserUpdate }) => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [name, setName] = useState(user?.name || '');
  const [avatarFile, setAvatarFile] = useState(null);
  const [removeAvatar, setRemoveAvatar] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(avatarUrl(user?.id, user?.avatar_v));
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Rest timer preferences (localStorage)
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [vibrateEnabled, setVibrateEnabled] = useState(true);

  useEffect(() => {
    try {
      const s = localStorage.getItem(PREF_SOUND);
      const v = localStorage.getItem(PREF_VIBRATE);
      setSoundEnabled(s === null ? true : s === '1');
      setVibrateEnabled(v === null ? true : v === '1');
    } catch (e) { console.warn('[EditProfilePage] non-critical failure', e); }
  }, []);

  const toggleSound = () => {
    const next = !soundEnabled;
    setSoundEnabled(next);
    try { localStorage.setItem(PREF_SOUND, next ? '1' : '0'); } catch (e) { console.warn('[EditProfilePage] non-critical failure', e); }
  };

  const toggleVibrate = () => {
    const next = !vibrateEnabled;
    setVibrateEnabled(next);
    try { localStorage.setItem(PREF_VIBRATE, next ? '1' : '0'); } catch (e) { console.warn('[EditProfilePage] non-critical failure', e); }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validar tipo
    if (!file.type.startsWith('image/')) {
      toast.error('Por favor, selecione uma imagem');
      return;
    }

    // Validar tamanho (max 10MB) — o servidor reduz para WebP < 100KB
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Imagem muito grande. Máximo 10MB');
      return;
    }

    if (previewUrl?.startsWith('blob:')) URL.revokeObjectURL(previewUrl);
    setAvatarFile(file);
    setRemoveAvatar(false);
    setPreviewUrl(URL.createObjectURL(file));
    setHasChanges(true);
  };

  const handleRemoveAvatar = () => {
    setAvatarFile(null);
    setRemoveAvatar(true);
    setPreviewUrl(null);
    setHasChanges(true);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleNameChange = (e) => {
    setName(e.target.value);
    setHasChanges(e.target.value !== user?.name);
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error('Nome é obrigatório');
      return;
    }

    setSaving(true);
    try {
      const nameChanged = name !== user?.name;
      const shouldDeleteAvatar = removeAvatar && !!user?.avatar_v;

      if (!nameChanged && !avatarFile && !shouldDeleteAvatar) {
        toast.info('Nenhuma alteração para salvar');
        setSaving(false);
        return;
      }

      const applyUser = (u) => { onUserUpdate(u); setStoredUser(u); };
      if (nameChanged) {
        applyUser((await axios.patch(`${API}/users/me/profile`, { name })).data);
      }
      if (avatarFile) {
        const form = new FormData();
        form.append('file', avatarFile);
        applyUser((await axios.post(`${API}/users/me/avatar`, form)).data);
      } else if (shouldDeleteAvatar) {
        applyUser((await axios.delete(`${API}/users/me/avatar`)).data);
      }
      
      toast.success('Perfil atualizado com sucesso!');
      setHasChanges(false);
      navigate(-1);
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error(error.response?.data?.detail || 'Erro ao atualizar perfil');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] pb-24 px-4 md:px-6 pt-6 md:pt-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-lg mx-auto"
      >
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button
            onClick={() => navigate(-1)}
            variant="ghost"
            className="text-zinc-400 hover:text-white p-2"
          >
            <ArrowLeft className="w-6 h-6" />
          </Button>
          <h1 className="font-primary font-black text-2xl text-white uppercase">
            Editar Perfil
          </h1>
        </div>

        {/* Avatar Section */}
        <div className="flex flex-col items-center mb-8">
          <div className="relative">
            <div className="w-32 h-32 rounded-full overflow-hidden bg-zinc-800 border-4 border-neon-lime/30">
              {previewUrl ? (
                <img
                  src={previewUrl}
                  alt="Avatar"
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <User className="w-16 h-16 text-zinc-600" />
                </div>
              )}
            </div>
            
            {/* Camera button */}
            <button
              onClick={() => fileInputRef.current?.click()}
              data-testid="avatar-change-btn"
              className="absolute bottom-0 right-0 w-10 h-10 bg-neon-lime rounded-full flex items-center justify-center shadow-lg hover:bg-neon-lime/80 transition-colors"
            >
              <Camera className="w-5 h-5 text-black" />
            </button>
          </div>
          
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            data-testid="avatar-file-input"
            className="hidden"
          />

          {previewUrl && (
            <Button
              onClick={handleRemoveAvatar}
              data-testid="avatar-remove-btn"
              variant="ghost"
              className="mt-3 text-red-400 hover:text-red-300 text-sm"
            >
              <X className="w-4 h-4 mr-1" />
              Remover foto
            </Button>
          )}
          
          <p className="text-zinc-500 text-sm mt-2">
            Toque no ícone para alterar a foto
          </p>
        </div>

        {/* Form */}
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-zinc-400 mb-2">
              Nome
            </label>
            <Input
              value={name}
              onChange={handleNameChange}
              placeholder="Seu nome"
              data-testid="profile-name-input"
              className="bg-zinc-900 border-zinc-800 text-white h-12 text-lg"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-zinc-400 mb-2">
              Email
            </label>
            <Input
              value={user?.email || ''}
              disabled
              className="bg-zinc-950 border-zinc-800 text-zinc-500 h-12"
            />
            <p className="text-xs text-zinc-600 mt-1">
              O email não pode ser alterado
            </p>
          </div>

          {/* Stats (read only) */}
          <div className="bg-zinc-900/50 border border-white/5 rounded-xl p-4 mt-8">
            <h3 className="text-sm font-medium text-zinc-400 mb-3">Suas estatísticas</h3>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <div className="font-primary font-black text-2xl text-neon-lime">{user?.level || 1}</div>
                <div className="text-xs text-zinc-500">Nível</div>
              </div>
              <div>
                <div className="font-primary font-black text-2xl text-white">{user?.streak || 0}</div>
                <div className="text-xs text-zinc-500">Streak</div>
              </div>
              <div className="flex flex-col items-center">
                <div className="flex items-center gap-2">
                  <img src="/fercoin.png" alt="FerCoin" className="w-6 h-6" />
                  <div className="font-primary font-black text-2xl text-yellow-400">{user?.coins || 0}</div>
                </div>
                <div className="text-xs text-zinc-500">FerCoin</div>
              </div>
            </div>
          </div>

          {/* Rest Timer Preferences */}
          <div className="bg-zinc-900/50 border border-white/5 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-zinc-400">Cronômetro de Descanso</h3>
              <button
                type="button"
                onClick={testRestSound}
                className="text-xs font-bold uppercase tracking-wider text-neon-lime hover:text-white bg-neon-lime/10 hover:bg-neon-lime/20 border border-neon-lime/30 rounded-full px-3 py-1.5 transition-all active:scale-95"
                data-testid="test-sound-btn"
              >
                Testar som
              </button>
            </div>
            <div className="space-y-3">
              <button
                type="button"
                onClick={toggleSound}
                className="w-full flex items-center justify-between bg-zinc-950/60 rounded-xl px-4 py-3 hover:bg-zinc-950 transition-colors"
                data-testid="toggle-sound-pref"
              >
                <div className="flex items-center gap-3 text-left">
                  <Volume2 className={`w-5 h-5 ${soundEnabled ? 'text-neon-lime' : 'text-zinc-600'}`} />
                  <div>
                    <p className="text-white text-sm font-bold">Som ao terminar</p>
                    <p className="text-zinc-500 text-xs">Toca um bipe quando o descanso acaba</p>
                  </div>
                </div>
                <div className={`relative w-11 h-6 rounded-full transition-colors ${soundEnabled ? 'bg-neon-lime' : 'bg-zinc-700'}`}>
                  <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-all ${soundEnabled ? 'left-[22px]' : 'left-0.5'}`} />
                </div>
              </button>

              <button
                type="button"
                onClick={toggleVibrate}
                className="w-full flex items-center justify-between bg-zinc-950/60 rounded-xl px-4 py-3 hover:bg-zinc-950 transition-colors"
                data-testid="toggle-vibrate-pref"
              >
                <div className="flex items-center gap-3 text-left">
                  <Vibrate className={`w-5 h-5 ${vibrateEnabled ? 'text-neon-lime' : 'text-zinc-600'}`} />
                  <div>
                    <p className="text-white text-sm font-bold">Vibração ao terminar</p>
                    <p className="text-zinc-500 text-xs">Vibra o aparelho quando o descanso acaba (mobile)</p>
                  </div>
                </div>
                <div className={`relative w-11 h-6 rounded-full transition-colors ${vibrateEnabled ? 'bg-neon-lime' : 'bg-zinc-700'}`}>
                  <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-all ${vibrateEnabled ? 'left-[22px]' : 'left-0.5'}`} />
                </div>
              </button>
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="mt-8">
          <Button
            onClick={handleSave}
            data-testid="profile-save-btn"
            disabled={saving || !hasChanges}
            className={`w-full h-14 text-lg font-bold ${
              hasChanges
                ? 'bg-neon-lime text-black hover:bg-neon-lime/80'
                : 'bg-zinc-800 text-zinc-500'
            }`}
          >
            {saving ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Salvando...
              </>
            ) : (
              <>
                <Save className="w-5 h-5 mr-2" />
                Salvar Alterações
              </>
            )}
          </Button>
        </div>
      </motion.div>
    </div>
  );
};
